import { Component, OnDestroy, ViewChild } from '@angular/core'
import { Subscription } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { Alert } from 'src/app/classes/alert.class'
import { AlertType } from 'src/app/enums/alert-type.enum'
import { ModalComponent } from 'src/app/components/modal/modal.component'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { EchargerService } from 'src/app/services/echarger.service'
import { map, takeWhile } from 'rxjs/operators'
import { TransactionService } from 'src/app/services/transaction.service'
import { Transaction } from 'src/app/classes/transaction.class'
import { UtilityService } from 'src/app/services/utility.service'
import { MessageEloaded } from 'src/app/enums/echarger-message.enum'
import { DateFormat } from 'src/app/enums/date-format.enum'
import { FormCanDeactivate } from '../../../guards/leave-page/form-can-deactivate'
import { SiteService } from 'src/app/services/site.service'

@Component({
  selector: 'app-virtual-echarger',
  templateUrl: './virtual-echarger.component.html',
  styleUrls: ['./virtual-echarger.component.scss']
})
export class VirtualEchargerComponent
  extends FormCanDeactivate
  implements OnDestroy {
  @ViewChild(ModalComponent) leaveModal: ModalComponent

  private subscriptions = new Array<Subscription>()
  private logSubscription: Subscription

  public loading = false
  public runningSequence = false
  public runeableSequence = false

  public alert: Alert

  public chargerStati = [
    `Handshaking`,
    `Preparing`,
    `Charging`,
    `Stopping`,
    `ChargeSuspended`,
    `Finished`,
    `Waiting`
  ]

  public commandTypes = [
    `81H-MaxCurrLimit#1`,
    `82H-MaxCurrLimit#3`,
  ]

  public plugStatii = [
    `Unplugged`,
    `Plugged`
  ]

  public protocolsSupported = ['109']
  public socket: number
  public connected = false
  public parks = new Array<{ parkName: string, zones: any[] }>()
  private sites = []
  public logs: string
  public showEchargerCodeWarning = false

  // buffered transaction to view one for each gun (4 guns)
  public transactionsCache = new Array<Transaction>(4)
  public transactionsView = new Array<Transaction>()

  // retrieved transactions
  public transactions = new Array<Transaction>(4)
  public echargerId: string

  public firstTransactions = new Array<string>()

  public Message = MessageEloaded
  public DateFormat = DateFormat

  // time measures to better adjust to target duration
  private seqPlanTocPre = 0
  private seqPlanTocPost = 0

  constructor (
    private _translate: TranslateService,
    private _echarger: EchargerService,
    private _site: SiteService,
    private _transaction: TransactionService,
    public _utility: UtilityService
  ) {
    super()
    this.init()
  }

  public logsForm: FormGroup = new FormGroup({
    filter: new FormControl(''),
    tail: new FormControl(40,
      [Validators.required, Validators.min(1), Validators.max(1000)]
    )
  })

  public sequenceForm: FormGroup = new FormGroup({
    duration: new FormControl(20,
      [Validators.required, Validators.min(1)]
    ),
    gunNr: new FormControl(1,
      [Validators.required, Validators.min(1), Validators.max(4)]
    ),
    socStart: new FormControl(1,
      [Validators.required, Validators.min(0), Validators.max(100)]
    ),
    socEnd: new FormControl(100,
      [Validators.required, Validators.min(0), Validators.max(100)]
    ),
    finishedState: new FormControl(true)
  })

  public connectionForm: FormGroup = new FormGroup({
    echargerCode: new FormControl('',
      [Validators.required, Validators.pattern('^[0-9a-zA-Z]*$')]
    ),
    zone: new FormControl('', Validators.required),
    prechargeLimit: new FormControl(undefined,
      [Validators.min(1), Validators.max(120)]
    ),
    chargingCapacity: new FormControl(480,
      [Validators.required, Validators.min(1), Validators.max(65535)]
    ),
    dischargingCapacity: new FormControl(0,
      [ Validators.required, Validators.min(0), Validators.max(65535)]
    )
  })

  // for input validation only
  public request44RegistrationForm: FormGroup = new FormGroup({
    protocol: new FormControl('109', Validators.required)
  })

  public response45ControlForm: FormGroup = new FormGroup({
    commandType: new FormControl('81H-MaxCurrLimit#1', Validators.required)
  })

  public response43PlugStatusForm: FormGroup = new FormGroup({
    gunNr: new FormControl(1,
      [Validators.required, Validators.min(1), Validators.max(4)]
    ),
    plugStatus: new FormControl('Unplugged', Validators.required)
  })

  public request42PrechargeForm: FormGroup = new FormGroup({
    gunNr: new FormControl(1,
      [Validators.required, Validators.min(1), Validators.max(4)]
    ),
    evccid: new FormControl('1234567890AB',
      [Validators.required, Validators.pattern('^[0-9a-fA-F]{12}$')]
    ),
    soc: new FormControl(33,
      [Validators.required, Validators.min(0), Validators.max(100)]
    ),
  })

  public response41ChargeInfoForm: FormGroup = new FormGroup({
    gunNr: new FormControl(1,
      [Validators.required, Validators.min(1), Validators.max(4)]
    ),
    evccid: new FormControl('1234567890AB',
      [Validators.required, Validators.pattern('^[0-9a-fA-F]{12}$')]
    ),
    soc: new FormControl(33,
      [Validators.required, Validators.min(0), Validators.max(100)]
    ),
    chargeStatus: new FormControl('', Validators.required),
    presentCurrent: new FormControl(10,
      [Validators.required, Validators.min(0), Validators.max(65535)]
    ),
    presentVoltage: new FormControl(220,
      [Validators.required, Validators.min(0), Validators.max(65535)]
    ),
    startValueAmmeter: new FormControl(5.3,
      [Validators.required, Validators.min(0.0), Validators.max(429496729.5)]
    ),
    realValueAmmeter: new FormControl(10.6,
      [Validators.required, Validators.min(0.0), Validators.max(429496729.5)]
    ),
    chargeTime: new FormControl(5,
      [Validators.required, Validators.min(0), Validators.max(65535)]
    ),
    targetCurrent: new FormControl(10,
      [Validators.required, Validators.min(0), Validators.max(65535)]
    ),
  })

  public response40HeartbeatForm: FormGroup = new FormGroup({
    firmware: new FormControl('1213',
      [Validators.required, Validators.pattern('^((?:[1-9]|1[0-5])[0-9]{2})$|^([1-9]{1}[0-9]{1})$|^([1-9]{1})$')]
    ),
    protocol: new FormControl('109', Validators.required),
    gunNr: new FormControl(1,
      [Validators.required, Validators.min(1), Validators.max(4)]
    )
  })

  public async init () {
    try {
      this.loading = true

      // -- fill zones in drop-down
      await this.loadSites()
      this.parks = this.sites.map(s => ({ parkName: s.name, zones: s.zones }))
      const selZone = this.sites
        .reduce((acc, s) => [...acc, ...s.zones], [])
        .filter(x => x)
        .find(({ name }) => name.toUpperCase().includes('VIRTUAL TESTING'))

      if (selZone) {
        this.connectionForm.patchValue({ zone: selZone._id })
      } else {
        this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.STATION.REQUIRES.ZONE.VIRTUAL_TESTING`)}`
        )
      }

      this.connectionForm.patchValue({ protocol: `109` })
      this.response41ChargeInfoForm.patchValue({ chargeStatus: `Charging` })
      this.response41ChargeInfoForm.markAsUntouched()

      this.request44RegistrationForm.disable()
      this.request42PrechargeForm.disable()
      this.response41ChargeInfoForm.disable()
      this.response45ControlForm.disable()
      this.response43PlugStatusForm.disable()

      this.loading = false
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${error.message}`
      )

      this.loading = false
    }
  }

  ngOnDestroy () {
    if (this.connected) {
      this.disconnectServer()
    }
    this.subscriptions.forEach(s => s.unsubscribe())
  }

  preventPageLeave (event) {
    event.preventDefault()
    event.returnValue = 'Please disconnect server first!'
    return event
  }

  public getModal (): ModalComponent {
    return this.leaveModal
  }

  public getForm () {
    return [this.connectionForm]
  }

  private markFormGroupTouched (formGroup: FormGroup): void {
    (<any>Object).values(formGroup.controls).forEach(control => {
      control.markAsTouched()

      if (control.controls) {
        this.markFormGroupTouched(control)
      }
    })
  }

  private openAlert (check: boolean, message: string): void {
    this.alert = new Alert(
      { type: (check ? AlertType.INFO : AlertType.DANGER), message }
    )
    this.alert.present()
  }

  public initializeLogs () {
    if (this.logSubscription) {
      this.logSubscription.unsubscribe()
    }
    const tail = this.logsForm.get(`tail`).value
    const logFilter = this.logsForm.get(`filter`).value

    this.logSubscription = this._echarger
      .virtualEcGetContinuosLogs(this.socket, logFilter, tail)
      .pipe(map(response => {
        if (response.invalid) {
          this.openAlert(false,
            `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${response.message}`
          )
        }
        return response.data
      }),
      takeWhile(() => this.connected, true)
      ).subscribe(log => {
        this.logs = log
      })

    this.subscriptions.push(this.logSubscription)
  }

  public async connectToServer (): Promise<void> {
    if (this.connectionForm.invalid) {
      this.openAlert(false, `${this._translate.instant(`ALERT.MESSAGE.VC.DIFFERENT_ECHARGER`)}`)
      return
    }

    try {
      this.connectionForm.markAsUntouched()
      const echargerCode = this.connectionForm.get(`echargerCode`).value.toUpperCase()
      const selectedZoneId = this.connectionForm.get(`zone`).value
      const prechargeLimit = this.connectionForm.get(`prechargeLimit`).value
      const chargingCapacity = this.connectionForm.get(`chargingCapacity`).value
      const dischargingCapacity = this.connectionForm.get(`dischargingCapacity`).value

      const response = await this._echarger
        .virtualEcConnect(
          selectedZoneId, echargerCode, prechargeLimit,
          chargingCapacity, dischargingCapacity
        )
        .toPromise()

      if (response.invalid) {
        this.openAlert(false,
          `${this._translate.instant(`${response.message}`)}.
          ${this._translate.instant(`ALERT.MESSAGE.VC.DIFFERENT_ECHARGER`)}`
        )
        return
      }

      this.echargerId = response.data.echarger
      this.socket = response.data.socket

      this.openAlert(true,
        `${this._translate.instant(`ALERT.MESSAGE.VC.CONNECTED`)} (Socket: ${this.socket})`
      )

      // -- successfully connected
      this.runeableSequence = false
      this.connected = true
      this.connectionForm.disable()
      this.response40HeartbeatForm.enable()
      this.response41ChargeInfoForm.enable()
      this.request42PrechargeForm.enable()
      this.response43PlugStatusForm.enable()
      this.request44RegistrationForm.enable()
      this.response45ControlForm.enable()

      // -- fill echarger code in filter
      this.logsForm.patchValue({ filter: `[${echargerCode.toUpperCase()}]` })

      // -- show special name warning
      this.showEchargerCodeWarning = echargerCode.startsWith(`H`) || echargerCode.startsWith(`SC`)

      // -- setup continous log loading
      this.initializeLogs()

      // -- setup transaction info view
      await this.initializeTransView()
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.SYSTEM_ERROR`)} ${error.statusText}!
        ${this._translate.instant(`ALERT.MESSAGE.VC.DIFFERENT_ECHARGER`)}`
      )
    }
  }

  public async disconnectServer (): Promise<void> {
    try {
      const response = await this._echarger
        .virtualEcDisconnect(this.socket).toPromise()

      if (response.invalid) {
        this.openAlert(false,
          `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${response.message}`
        )
        return
      }

      this.openAlert(true,
        `${this._translate.instant(`ALERT.MESSAGE.VC.DISCONNECTED`)}`
      )

      this.runningSequence = false
      this.runeableSequence = false
      this.connected = false
      this.connectionForm.enable()
      this.response40HeartbeatForm.disable()
      this.response41ChargeInfoForm.disable()
      this.request42PrechargeForm.disable()
      this.response43PlugStatusForm.disable()
      this.request44RegistrationForm.disable()
      this.response45ControlForm.disable()

      // -- the page may savefely be closed by the browser if just disconnected
      this.connectionForm.markAsPristine()

      this.showEchargerCodeWarning = false

      this.logSubscription.unsubscribe()

      this.logsForm.patchValue({ filter: `` })

      this.transactions = undefined
      this.transactionsCache = new Array<Transaction>(4)
      this.transactionsView = undefined
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.SYSTEM_ERROR`)} ${error.statusText}`
      )
    }
  }

  public async connectOrDisconnectServer (): Promise<void> {
    if (this.connectionForm.invalid) {
      return this.markFormGroupTouched(this.connectionForm)
    }
    if (!this.connected) {
      await this.connectToServer()
    } else {
      await this.disconnectServer()
    }
  }

  public async responseHeartbeat40 (): Promise<void> {
    if (this.response40HeartbeatForm.invalid) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.INVALID_INPUT`)} (HeartBeat)`
      )
      return
    }

    try {
      const response = await this._echarger.virtualEcResponseHeartbeat(
        this.socket,
        this.response40HeartbeatForm.value
      ).toPromise()

      if (response.invalid) {
        this.openAlert(false,
          `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${response.message}`
        )
        return
      }

      this.openAlert(true,
        `${this._translate.instant(`ALERT.MESSAGE.VC.RESPONSE.HEARTBEAT`)} ${response.message}`
      )
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.SYSTEM_ERROR`)} ${error.statusText}`
      )
    }
  }

  public async responseChargeInfo41 (): Promise<void> {
    if (this.response41ChargeInfoForm.invalid) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.INVALID_INPUT`)} (41)`
      )
      return
    }

    try {
      const response = await this._echarger.virtualEcResponseChargeInfo(
        this.socket,
        this.response41ChargeInfoForm.value
      ).toPromise()

      if (response.invalid) {
        this.openAlert(false,
          `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${response.message}`
        )
        return
      }

      this.openAlert(true,
        `${this._translate.instant(`ALERT.MESSAGE.VC.RESPONSE.CHARGEINFO`)} ${response.message}`
      )

      await this.initializeTransView()
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.SYSTEM_ERROR`)} ${error.statusText}`
      )
    }
  }

  public async requestPrecharge42 (): Promise<void> {
    if (this.request42PrechargeForm.invalid) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.INVALID_INPUT`)} (42)`
      )
      return
    }

    try {
      const response = await this._echarger.virtualEcRequestPrecharge(
        this.socket,
        this.request42PrechargeForm.value
      ).toPromise()

      if (response.invalid) {
        this.openAlert(false,
          `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${response.message}`
        )
        return
      }

      this.openAlert(true,
        `${this._translate.instant(`ALERT.MESSAGE.VC.REQUEST.PRECHARGE`)} ${response.message}`
      )

      await this.initializeTransView()
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.SYSTEM_ERROR`)} ${error.statusText}`
      )
    }
  }

  public async responsePlugStatus43 (): Promise<void> {
    if (this.response43PlugStatusForm.invalid) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.INVALID_INPUT`)} (43)`
      )
      return
    }

    try {
      const response = await this._echarger.virtualEcResponsePlugStatus(
        this.socket,
        this.response43PlugStatusForm.value
      ).toPromise()

      if (response.invalid) {
        this.openAlert(false,
          `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${response.message}`
        )
        return
      }

      this.openAlert(true,
        `${this._translate.instant(`ALERT.MESSAGE.VC.RESPONSE.PLUG_STATUS`)} ${response.message}`
      )

      await this.initializeTransView()
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.SYSTEM_ERROR`)} ${error.statusText}`
      )
    }
  }

  public async requestRegistration44 (): Promise<void> {
    const protocolVersion = this.request44RegistrationForm.get(`protocol`).value
    try {
      const response = await this._echarger.virtualEcRequestRegister(
        this.socket,
        protocolVersion
      ).toPromise()

      if (response.invalid) {
        this.openAlert(false,
          `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${response.message} ${response.data}`
        )
        return
      }

      this.openAlert(true,
        `${this._translate.instant(`ALERT.MESSAGE.VC.REQUEST.REGISTERED`)} ${response.message}`
      )
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.SYSTEM_ERROR`)} ${error.statusText}`
      )
    }
  }

  public async responseControl45 (): Promise<void> {
    if (this.response45ControlForm.invalid) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.INVALID_INPUT`)} (45)`
      )
      return
    }

    try {
      const response = await this._echarger.virtualEcResponseControl(
        this.socket,
        this.response45ControlForm.value
      ).toPromise()

      if (response.invalid) {
        this.openAlert(false,
          `${this._translate.instant(`ALERT.MESSAGE.INVALID`)} ${response.message}`
        )
        return
      }

      this.openAlert(true,
        `${this._translate.instant(`ALERT.MESSAGE.VC.RESPONSE.CONTROL`)} ${response.message}`
      )

      await this.initializeTransView()
    } catch (error) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.SYSTEM_ERROR`)} ${error.statusText}`
      )
    }
  }

  public transactionsFound (gun?: number): boolean {
    return gun !== undefined
      ? !!(this.transactions && this.transactions
        .filter((transaction) => transaction.gun === gun).length)
      : !!(this.transactions && this.transactions
        .filter((transaction) => transaction).length)
  }

  public transactionsViewsFound (gun?: number): boolean {
    return gun !== undefined
      ? !!(this.transactionsView && this.transactionsView
        .filter((transaction) => transaction.gun === gun).length)
      : !!(this.transactionsView && this.transactionsView
        .filter((transaction) => transaction).length)
  }

  public async initializeTransView () {
    await new Promise(resolve => setTimeout(resolve, 2000))

    const responseConnection = await this._transaction
      .getEchargersConnection(this.echargerId).toPromise()

    if (responseConnection.invalid) {
      if (responseConnection.message === 'ERROR.NO_CONNECTION_FOUND') {
        responseConnection.data.transactions = []
      } else {
        return this.openAlert(false,
          `${this._translate.instant('ALERT.MESSAGE.INVALID')} ` +
          `${this._translate.instant(responseConnection.message)}`
        )
      }
    }

    const closedIndex = await this.firstTransactions.findIndex(f => f.startsWith('!'))
    if (
      closedIndex !== -1 &&
      await this.transactions.findIndex(t =>
        t._id === this.firstTransactions[closedIndex].slice(1)
      ) !== -1
    ) {
      for (let i = 0; i < 8; i++) {
        this.firstTransactions[i] = i === closedIndex
          ? this.firstTransactions[i]
          : ''
      }
    } else {
      this.firstTransactions = ['', '', '', '']
    }

    this.transactions = responseConnection.data.transactions

    // store transaction with gunNr in view buffer
    this.transactions.forEach( trans => {
      if (trans) {
        [0, 1, 2, 3].forEach(i => {
          if (trans.gun === i + 1) {
            this.transactionsCache[i] = trans
          }
        })
      }
    })

    this.transactionsView = this.transactionsCache.filter(t => t !== undefined)

    this.transactionsView.forEach(tv => {
      if (this.transactions.filter(t => t.gun === tv.gun).length === 0) {
        if (tv.data.length > 0) {
          tv.data[tv.data.length - 1].chargeStatus = `WasFinished`
        }
        tv.active = false
      }
    })
    this.runeableSequence = this.checkForRuneableSequence()
  }

  private transRunnable (trans, gunNr?): boolean {
    if (trans) {
      if (gunNr !== undefined && trans.gun !== gunNr) {
        return false
      }

      if (!trans.data.length) {
        return trans.status === 'PRECHARGING'
      } else {
        return trans.data[trans.data.length - 1].chargeStatus !== 'WasFinished'
      }
    }
  }

  public isATransactionInPrecharge (): boolean {
    if (!this.transactionsView) {
      return false
    }

    const runnable = this.transactionsView.some(t => this.transRunnable(t))
    return runnable
  }

  public checkForRuneableSequence (): boolean {
    return this.isATransactionInPrecharge() && !this.runningSequence
  }

  public calculateKW (current: number, voltage: number): number {
    return Math.round(current * voltage / 100) / 10
  }

  public noAlarms (alarms: any[]) {
    return alarms.every(a => a === 'No alarm')
  }

  public notAutoOpen (index: number, id: string, closed: boolean) {
    const tr = this.firstTransactions[index]
    if (tr === id || tr.slice(1) === id) {
      if (closed) {
        this.firstTransactions[index] = '!' + id
      } else {
        // remove ! if tab is openend again, doesnt need an extra icon since
        // only one can be active
        if (tr.startsWith('!')) {
          this.firstTransactions[index] = tr.slice(1)
        }
      }
    }
  }

  public copyMessage (message: string): void {
    const cb = 'clipboard'
    const clipboard = window.navigator[cb]

    clipboard.writeText(message)
  }

  public randomEvccid (): void {
    const evccid = 'x'
      .repeat(12)
      .replace(/x/g, () => Math.floor(Math.random() * 16).toString(16))
      .toUpperCase()

    this.request42PrechargeForm.patchValue({ evccid })
    this.response41ChargeInfoForm.patchValue({ evccid })
  }

  private async loadSites (page = 0) {
    const res = await this._site.searchSite({ page }).toPromise()
    if (res.invalid) {
      throw new Error(res.message)
    }
    this.sites = page
      ? [...this.sites, ...res.data.data]
      : res.data.data
    if (res.data.hasNext) {
      this.loadSites(page + 1)
    }
  }

  private async waitSecs (secs) {
    return new Promise(resolve => setTimeout(resolve, secs * 1000))
  }

  private async connectionInterrupted () {
    if (!this.connected) {
      this.openAlert(false, `${this._translate.instant(`Connection interrupted, exiting sequence`)}`)
      return true
    }
    return false
  }

  public async executePlanOld () {
    const virtualELoadedPlan = {
      durationSecs: 10,
      plan: [
        { at: 10, chargeInfo: { soc: 1, presentCurrent: 20 } },
        { at: 12, chargeInfo: {
          soc: 2,
          presentCurrent: 15,
          presentVoltage: 210,
          startValueAmmeter: 10.6,
          realValueAmmeter: 21.4,
          chargeTime: 10,
          targetCurrent: 20
        } },
        { at: 20, chargeInfo: { soc: 5 } },
        { at: 30, chargeInfo: { soc: 12 }},
        { at: 35, chargeInfo: { soc: 20, presentCurrent: 30 }  },
        { at: 55, chargeInfo: { soc: 31 } },
        { at: 65, chargeInfo: { soc: 43 } },
        { at: 70, chargeInfo: { soc: 51, presentCurrent: 35 } },
        { at: 72, chargeInfo: { soc: 54, presentCurrent: 30 } },
        { at: 75, chargeInfo: {
          soc: 64,
          presentVoltage: 200,
          presentCurrent: 42
        } },
        { at: 84, chargeInfo: { soc: 76 } },
        { at: 85, chargeInfo: { soc: 81, presentCurrent: 40 } },
        { at: 90, chargeInfo: { soc: 96, presentCurrent: 60 }  },
        { at: 100, chargeInfo: {
          soc: 100,
          presentCurrent: 80,
          presentVoltage: 100,
          chargeStatus: 'Finished'
        } }
      ]
    }

    let duration = this.sequenceForm.get(`duration`).value
    if (duration === 0) {
      duration = virtualELoadedPlan.durationSecs
    }

    console.log(`Executing plan with duration ${duration} secs`)

    const tic = Math.floor(Date.now() / 1000)
    console.log(`Start at: ${tic}`)

    let lastAt = 0
    for (const { chargeInfo, at } of virtualELoadedPlan.plan) {
      for (const [key, value] of Object.entries(chargeInfo)) {
        if (this.response41ChargeInfoForm.contains(key)) {
          this.response41ChargeInfoForm.patchValue({ [key]: value })
        }
      }
      const scaledAt = at / 100.0 * duration * 0.0
      const diffSecs = scaledAt - lastAt

      // omit decreasing progression step (should be sorted)
      if (diffSecs < 0) {
        continue
      }
      lastAt = scaledAt
      await this.waitSecs(diffSecs) // TODO wait only, if needed ...
      await this.responseChargeInfo41()
      if (await this.connectionInterrupted()) {
        this.runningSequence = false
        return
      }
    }
    if (await this.connectionInterrupted()) {
      this.runningSequence = false
      return
    }

  }

  // -- get timestamp in seconds
  private tic () { return Math.floor(Date.now() / 1000) }

  public async executePlanNew (seqGunNr) {

    // -- config vars
    const duration = this.sequenceForm.get(`duration`).value
    const socStart = this.sequenceForm.get(`socStart`).value
    const socEnd = this.sequenceForm.get(`socEnd`).value
    // const allowedCurrentIncrementSteps = 5
    const targetIntervalSecs = 2

    // -- state vars
    let soc = socStart
    let ammeter = this.response41ChargeInfoForm.get(`startValueAmmeter`).value

    const prePostDurations = this.seqPlanTocPre + this.seqPlanTocPost
    const targetSecs = Math.max(0, duration - prePostDurations)

    // -- some hardcoded values for now
    let voltage = 220
    const maxCurrentProtocol = 65535
    // note for fast charging this maybe should be 400V or 800V and dynamic
    this.response41ChargeInfoForm.patchValue({ presentVoltage: voltage })
    const batteryCapacityKWh = 20 // hardcoded for now

    // -- timing vars
    const tic = this.tic()
    const toc = () => this.tic() - tic

    this.response41ChargeInfoForm.patchValue({ soc: socStart })
    this.response41ChargeInfoForm.patchValue({ realValueAmmeter: ammeter })

    while (soc < socEnd) {
      voltage = 220
      // -- calculate soc progression
      const elapsedSecs = toc() === 0 ? 2 : toc()

      soc = Math.min(
        socEnd,
        socStart + Math.floor(socEnd - socStart) * elapsedSecs / targetSecs
      )
      const socIncrement = soc - this.response41ChargeInfoForm.get(`soc`).value

      // assume that 1% soc equals batteryCapacityKWh / 100 kWh of energy
      const energyIncrement = batteryCapacityKWh / 100.0 * socIncrement

      // -- wait to meet interval timing
      const waitSecs = Math.max(0, targetIntervalSecs - (toc() - elapsedSecs))

      if (waitSecs > 0) {
        await this.waitSecs(waitSecs)
      }

      // compute current needed to meet the soc increment
      const duration = targetIntervalSecs // this.tic() - localTic
      let current =
          Math.ceil(energyIncrement * 1000.0 * 3600.0 / voltage / duration)
      if (current > maxCurrentProtocol) {
        current = maxCurrentProtocol
        // adjust voltage to meet energyForSocIncrementKWh
        voltage =
          Math.ceil(energyIncrement * 1000.0 * 3600.0 / current / duration)
        this.response41ChargeInfoForm.patchValue(
          { presentVoltage: Math.round(voltage) }
        )
      }

      this.response41ChargeInfoForm.patchValue(
        { presentCurrent: Math.round(current) }
      )
      this.response41ChargeInfoForm.patchValue(
        { soc: Math.round(soc) }
      )

      const elapsedMinsDec = Math.max(1, Math.round(elapsedSecs / 60.0))
      this.response41ChargeInfoForm.patchValue(
        { chargeTime: elapsedMinsDec  }
      )

      const ammeterIncrement = (current * voltage * duration / 3600.0) / 1000.0
      ammeter += ammeterIncrement

      this.response41ChargeInfoForm.patchValue(
        { realValueAmmeter: Math.round(ammeter * 10) / 10 } )

      await this.responseChargeInfo41()
      if (await this.connectionInterrupted()) {
        this.runningSequence = false
        return
      }
    }
  }

  public async executeSequence () {
    if (this.sequenceForm.invalid ||
        this.sequenceForm.get(`socEnd`).value <= this.sequenceForm.get(`socStart`).value
    ) {
      this.openAlert(false,
        `${this._translate.instant(`ALERT.MESSAGE.INVALID_INPUT`)} (Sequence)`
      )
      return
    }
    const gunNr = this.sequenceForm.get(`gunNr`).value
    if (!this.transactionsView.some(
      t => this.transRunnable(t, gunNr) ) ) {
      this.openAlert(false,
        `${this._translate.instant(
          `ALERT.MESSAGE.NO_PRECHARGING_TRANSACTION_AT_GUN_NR`)} ${gunNr}`
      )
      return
    }

    this.openAlert(true,
          `${this._translate.instant(
            `ALERT.MESSAGE.STARTING_SEQUENCE_AT_GUN_NR`)} ${gunNr}`
        )

    // -- cache charge info values
    const saveMeterState = this.response41ChargeInfoForm.value
    const oldGunNr = this.request42PrechargeForm.get(`gunNr`).value
    const oldPrechargeSoc = this.request42PrechargeForm.get(`soc`).value

    // -- default configs for sequence execution
    const seqGunNr = this.sequenceForm.get(`gunNr`).value

    // -- execute sequence
    this.response41ChargeInfoForm.patchValue({ chargeStatus: 'Charging' })
    this.request42PrechargeForm.patchValue({ gunNr: seqGunNr })
    this.response41ChargeInfoForm.patchValue({ gunNr: seqGunNr })

    this.runningSequence = true
    this.runeableSequence = false
    this.seqPlanTocPre = 0

    await this.executePlanNew(seqGunNr)

    if (this.sequenceForm.get(`finishedState`).value) {
      this.response41ChargeInfoForm.patchValue({ chargeStatus: 'Finished' })
      await this.responseChargeInfo41()
    }

    // -- restore cached meter values
    this.response41ChargeInfoForm.patchValue(saveMeterState)
    this.response41ChargeInfoForm.markAsPristine()
    this.request42PrechargeForm.patchValue({ gunNr: oldGunNr })
    this.request42PrechargeForm.markAsPristine()
    this.request42PrechargeForm.patchValue({ soc: oldPrechargeSoc })
    this.request42PrechargeForm.markAsPristine()

    await this.initializeTransView()
    this.runningSequence = false
    this.runeableSequence = this.checkForRuneableSequence()
  }
}
